import { useQuery } from '@tanstack/react-query';
import { Bot, Calculator, ClipboardList, Ruler } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { Logo } from '../components/Logo';
import { ApiError, api } from '../lib/api';
import { SUPPORT } from '../lib/contact';

function AuthLayout({ title, children, footer }: { title: string; children: ReactNode; footer: ReactNode }) {
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden overflow-hidden bg-navy-900 p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '32px 32px' }}
        />
        <div className="relative w-fit rounded-xl bg-white p-3">
          <Logo className="h-auto w-64" />
        </div>
        <div className="relative max-w-md">
          <h2 className="text-3xl leading-tight font-bold">
            Du métré au devis final,
            <br />
            <span className="text-brand-500">sans calcul manuel répétitif.</span>
          </h2>
          <ul className="mt-6 space-y-3 text-sm text-white/80">
            <li className="flex gap-3"><Ruler size={18} className="shrink-0 text-brand-500" /> Métré paramétrique multi-niveaux : S1, P4, V2… créés par l'ingénieur</li>
            <li className="flex gap-3"><Calculator size={18} className="shrink-0 text-brand-500" /> Chaque quantité justifiée : « Voir le calcul »</li>
            <li className="flex gap-3"><ClipboardList size={18} className="shrink-0 text-brand-500" /> DQE, déboursé, prix de vente, montant en lettres</li>
            <li className="flex gap-3"><Bot size={18} className="shrink-0 text-brand-500" /> ExpertDevis AI explique, contrôle et propose — sans inventer de chiffres</li>
          </ul>
        </div>
        <p className="relative text-xs text-white/50">Le chiffrage intelligent de tous vos projets BTP.</p>
      </section>
      <section className="flex items-center justify-center bg-white px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Logo className="h-auto w-56" />
          </div>
          <h1 className="mb-6 text-2xl font-bold text-navy-900">{title}</h1>
          {children}
          <div className="mt-6 text-center text-sm text-muted">{footer}</div>
        </div>
      </section>
    </div>
  );
}

function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  const message =
    error instanceof ApiError && error.code === 'VALIDATION' && Array.isArray(error.details)
      ? (error.details as Array<{ message: string }>).map((d) => d.message).join(' · ')
      : error instanceof Error
        ? error.message
        : 'Erreur inattendue';
  return <div className="mb-4 rounded-lg border border-bad/30 bg-bad/5 px-3 py-2 text-sm text-bad">{message}</div>;
}

/** Ouverte tant que le serveur n'a pas répondu autrement : une panne ne doit pas cacher le lien à tort. */
function useSignupOpen() {
  const options = useQuery({ queryKey: ['auth-options'], queryFn: () => api<{ allowSignup: boolean }>('/auth/options'), retry: false, staleTime: 300_000 });
  return options.data?.allowSignup ?? true;
}

export function LoginPage() {
  const signupOpen = useSignupOpen();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({ email, password });
      navigate('/');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Connexion" footer={<><Link className="mb-2 block text-tech-600" to="/mot-de-passe-oublie">Mot de passe oublié ?</Link>{signupOpen && <>Pas encore de compte ? <Link className="font-semibold text-tech-600" to="/inscription">Créer mon entreprise</Link></>}</>}>
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox error={error} />
        <div>
          <label className="label" htmlFor="email">Adresse e-mail</label>
          <input id="email" className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="password">Mot de passe</label>
          <input id="password" className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button className="btn-primary w-full" disabled={busy}>{busy ? 'Connexion…' : 'Se connecter'}</button>
      </form>
    </AuthLayout>
  );
}

export function SignupPage() {
  const signupOpen = useSignupOpen();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const countries = useQuery({ queryKey: ['countries'], queryFn: () => api<Array<{ code: string; name: string; defaultCurrency: string }>>('/countries', { auth: false }) });
  const [form, setForm] = useState({ companyName: '', countryCode: 'TD', fullName: '', email: '', password: '' });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signup(form);
      navigate('/');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Créer mon entreprise" footer={<>Déjà inscrit ? <Link className="font-semibold text-tech-600" to="/connexion">Se connecter</Link></>}>
      {!signupOpen ? (
        <p className="text-sm text-muted">Les inscriptions en ligne ne sont pas ouvertes pour le moment. Pour obtenir un accès, contactez l'équipe ExpertDevis BTP : <a className="font-semibold text-tech-600 hover:underline" href={`mailto:${SUPPORT.email}`}>{SUPPORT.email}</a> · <a className="font-semibold text-tech-600 hover:underline" href={SUPPORT.whatsappUrl} target="_blank" rel="noreferrer">WhatsApp {SUPPORT.phone}</a>.</p>
      ) : (
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox error={error} />
        <div>
          <label className="label" htmlFor="company">Entreprise / bureau d'études</label>
          <input id="company" className="input" required value={form.companyName} onChange={set('companyName')} />
        </div>
        <div>
          <label className="label" htmlFor="country">Pays</label>
          <select id="country" className="input" value={form.countryCode} onChange={set('countryCode')}>
            {(countries.data ?? [{ code: 'TD', name: 'Tchad', defaultCurrency: 'XAF' }]).map((c) => (
              <option key={c.code} value={c.code}>{c.name} ({c.defaultCurrency === 'XAF' || c.defaultCurrency === 'XOF' ? `FCFA ${c.defaultCurrency}` : c.defaultCurrency})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="fullName">Votre nom</label>
          <input id="fullName" className="input" required value={form.fullName} onChange={set('fullName')} />
        </div>
        <div>
          <label className="label" htmlFor="email">Adresse e-mail</label>
          <input id="email" className="input" type="email" autoComplete="username" required value={form.email} onChange={set('email')} />
        </div>
        <div>
          <label className="label" htmlFor="password">Mot de passe</label>
          <input id="password" className="input" type="password" autoComplete="new-password" required value={form.password} onChange={set('password')} />
          <p className="mt-1 text-xs text-muted">10 caractères minimum, dont une lettre et un chiffre.</p>
        </div>
        <button className="btn-primary w-full" disabled={busy}>{busy ? 'Création…' : 'Créer le compte'}</button>
      </form>
      )}
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  return (
    <AuthLayout title="Mot de passe oublié" footer={<Link className="font-semibold text-tech-600" to="/connexion">Retour à la connexion</Link>}>
      <p className="text-sm text-muted">Demandez à l'administrateur de votre entreprise un lien de réinitialisation : il le crée depuis Paramètres, Utilisateurs et rôles, et vous le transmet (message, WhatsApp). Le lien sert une seule fois, pendant 24 heures.</p>
      <p className="mt-3 text-sm text-muted">Si vous êtes le seul administrateur, contactez l'équipe ExpertDevis BTP : <a className="font-semibold text-tech-600 hover:underline" href={`mailto:${SUPPORT.email}`}>{SUPPORT.email}</a> · <a className="font-semibold text-tech-600 hover:underline" href={SUPPORT.whatsappUrl} target="_blank" rel="noreferrer">WhatsApp {SUPPORT.phone}</a>.</p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { token = '' } = useParams();
  const status = useQuery({ queryKey: ['reset', token], queryFn: () => api<{ valid: boolean }>(`/auth/reset/${encodeURIComponent(token)}`), retry: false });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) return setError(new Error('Les deux mots de passe sont différents.'));
    setBusy(true);
    try {
      await api('/auth/reset', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Nouveau mot de passe" footer={<Link className="font-semibold text-tech-600" to="/connexion">Aller à la connexion</Link>}>
      {done ? (
        <p className="text-sm text-ok">Mot de passe modifié. Vous pouvez vous connecter.</p>
      ) : status.data && !status.data.valid ? (
        <p className="text-sm text-bad">Ce lien n'est plus valable. Demandez-en un nouveau à votre administrateur.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorBox error={error ?? status.error} />
          <label className="block text-sm font-medium">Nouveau mot de passe<input className="input mt-1" type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} /></label>
          <label className="block text-sm font-medium">Confirmation<input className="input mt-1" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} /></label>
          <p className="text-xs text-muted">10 caractères au moins, avec une lettre et un chiffre.</p>
          <button className="btn-primary w-full justify-center" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        </form>
      )}
    </AuthLayout>
  );
}
